import React, { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import Navbar from './Navbar';
import Sidebar from './Sidebar';
import labService from './labService';
import { getApiErrorMessage } from '../auth/apiError';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faChevronDown, faChevronUp } from '@fortawesome/free-solid-svg-icons';
import './css/Lab.css';
import './css/ChooseModel.css';
import './css/ModelsInfo.css';
import token_red from './images/token-red.png'
import token_green from './images/token_nqtb.png'

const LOAD_ERROR = 'The model list could not be loaded. Please refresh the page.';

// Only a network failure says more than the fixed sentence; server texts aren't for users.
const loadErrorMessage = (error) => (error.response ? LOAD_ERROR : getApiErrorMessage(error, LOAD_ERROR));

const ModelsInfo = ({ isCollapsed, toggleSidebar }) => {
    const [endpoints, setEndpoints] = useState(null);
    const [modelsList, setModelsList] = useState([]);
    const [error, setError] = useState(null);
    const [expandedModelId, setExpandedModelId] = useState(null);
    const [user, setUser] = useState(null);
    const [loading, setLoading] = useState(true);
    const navigate = useNavigate();


    useEffect(() => {
        const user = JSON.parse(localStorage.getItem('user'));

        if (!user) {
            setError("You must be logged in to view this page.");
            return;
        }

        const fetchModels = async () => {
            try {
                const response = await labService.getModels();
                setModelsList(response.data);
                console.log(response.data);

            } catch (error) {
                console.error('Error fetching prediction details:', error);
                setError(loadErrorMessage(error));
            }
        };

        const fetchEndpoints = async () => {
            try {
                const response = await labService.getEndpoints();
                console.log(response.data);
                setEndpoints(response.data);

            } catch (error) {
                console.error('Error fetching endpoint details:', error);
                setError(loadErrorMessage(error));
            }
        };
        
        fetchEndpoints();
        fetchModels();
    }, []);

    useEffect(() => {
        const fetchUserData = async () => {
            const userData = localStorage.getItem('user');
            if (!userData) {
                setLoading(false);
                return;
            }

            try {
                const response = await labService.getUserInfo();
                setUser(response.data);
            } catch (error) {
                console.error('Error fetching user data', error);
            } finally {
                setLoading(false);
            }
            
        };

        fetchUserData();
    }, []);


    const toggleExpand = (modelId) => {
        setExpandedModelId((prevId) => (prevId === modelId ? null : modelId));
    };

    const handleNewPrediction = (model_id) => {
        localStorage.setItem('selectedModel', JSON.stringify(model_id));
        navigate('/new-pred');
    };

    const handleClose = () => {
        navigate(-1); // Navigate to the previous page
    };

    const formatKey = (key) => {
        if (key === 'Q^2_LOO') {
            return (
                <>
                    Q<sup>2</sup><sub>LOO</sub>
                </>
            );
        } else if (key === 'R^2') {
            return (
                <>
                    R<sup>2</sup>
                </>
            );
        } else if (key === 'CCC_CV') {
            return (
                <>
                    CCC<sub>CV</sub>
                </>
        );
        } else if (key === 'Q^2_F1') {
            return (
                <>
                    Q<sup>2</sup><sub>F1</sub>
                </>
            );
        } else if (key === 'Q^2_F2') {
            return (
                <>
                    Q<sup>2</sup><sub>F2</sub>
                </>
            );
        } else if (key === 'Q^2_F3') {
            return (
                <>
                    Q<sup>2</sup><sub>F3</sub>
                </>
            );
        } else if (key === 'CCC_EXT') {
            return (
                <>
                    CCC<sub>EXT</sub>
                </>
        );
        }
    
        return key; // Return the key as-is if no formatting is required
    };

    const getStaticUrl = (filename) => {
        // Assuming your Django backend is on port 8000 in development
        const backendUrl = process.env.REACT_APP_API_URL || 'http://localhost:8000';
        return `${backendUrl}static/qmrf/${filename}`;
    };


    return (
        <div className='lab-container'>
            <Navbar />
            <div className='lab-side-content'>
                <Sidebar active_n={4} isCollapsed={isCollapsed} toggleSidebar={toggleSidebar} />
                <div className="choose-content">
                    <h2 className='aval-endp'>Models Information</h2>
                    
                        {error ? (
                            <p className='lab-error' role='alert'>{error}</p>
                        ) : endpoints && modelsList ? (
                            <div className='models-cont-mi'>
                                {endpoints
                                .filter((endpoint) => 
                                    modelsList.some((model) => model.endpoint_type === endpoint.id))
                                .map((endpoint) => (
                                <div>
                                    <div className="up-mod-cont-mi">
                                        <img className="img-end-small" src={endpoint.img_url} alt={endpoint.endpoint_name} />
                                        <p className="endpoint-name">{endpoint.endpoint_name}</p>
                                    </div>

                                    <div className="mdls-lst">
                                        {modelsList
                                        .filter((model) => model.endpoint_type === endpoint.id)
                                        .map((model) => (
                                            <div key={model.id} className="model-row">
                                                <div className="model-header" onClick={() => toggleExpand(model.id)}>
                                                    <div className='name-chevron'>
                                                        <FontAwesomeIcon
                                                            className="chevron-icon"
                                                            icon={expandedModelId === model.id ? faChevronUp : faChevronDown}
                                                        />
                                                        <p className='mdl-name'>{model.model_name}</p>
                                                    </div>
                                                </div>
                                                {expandedModelId === model.id && (
                                                <div className="model-detale-mi">
                                                    <div className='detail-1'>
                                                        <p className='det-main'>Model description</p>
                                                        <p className='det-p'>{model.model_description}</p>
                                                        <p className='det-sec'>Type of model</p>
                                                        <p className='det-p'>{model.method_type}</p>
                                                        <p className='det-sec'>Descriptors</p>
                                                        <p className='det-p' style={{ whiteSpace: 'pre-line' }}>
                                                            {model.model_details.descriptors_desc?.join('\n')}
                                                        </p>

                                                        <p className='det-sec'>Statistics</p>
                                                        <div className="det-p">
                                                            {Object.entries(model.model_details.statistics).map(([key, value]) => (
                                                                <div key={key}>
                                                                <strong>{formatKey(key)}</strong>: {value}
                                                                </div>
                                                            ))}
                                                        </div>
                                                        
                                                    </div>
                                                    <div className='detail-2'>
                                                        <p className='det-main'>Endpoint description</p>
                                                        <p className='det-p'>{model.model_details.endpoint_desc}</p>
                                                        <p className='det-main'>Type of compounds for model applicability</p>
                                                        <p className='det-p'>{model.model_details.model_applicability}</p>
                                                        <p className='det-main'>QMRF</p>
                                                        {model.model_details?.qmrf_filename && (
                                                        <p className='det-p'>
                                                            <strong>QMRF Report: </strong>
                                                            <a 
                                                            href={getStaticUrl(model.model_details.qmrf_filename)}
                                                            target="_blank" 
                                                            rel="noopener noreferrer"
                                                            className="qmrf-link"
                                                            >
                                                            View PDF Report ↗
                                                            </a>
                                                        </p>
                                                        )}
                                                        <p className='det-main'>Cost</p>
                                                        <div className='cost-div-mi'>

                                                            <p className={`cost-mi`}>
                                                                {`${model.model_price} ${model.model_price === 1 ? 'token' : 'tokens'}`}
                                                            </p>
                                                            <img
                                                                className="token-cost-mi"
                                                                src={token_green}
                                                                alt="token icon"
                                                            />
                                                            

                                                        </div>
                                                    </div>
                                                    <div className='detail-3'>
                                                        
                                                    </div>                                            
                                                </div>
                                                )}
                                            </div>
                                        ))}
                                    </div>
                                </div>
                                ))}
                            </div>
                        
                            

                        ): (<></>)}
                    
                    
                </div>    
            </div>
        </div>
    );
};

export default ModelsInfo;